import { arrowLeftCircleIcon, icon, xCircleFillIcon12 } from './icons';
import { identicon } from './identicon';
import { logoMark } from './logo-mark';
import {
  anonymousUserId,
  cumulusParticipantOrganizationId,
  cumulusSystemOrganizationId,
  cumulusSystemUserId
} from './model/user';
import { cumulusSupportLoginRegex } from './staff';
import { TemplateResult, attr, html } from './template';

export type AvatarSize =
  | 16
  | 20
  | 24
  | 32
  | 40
  | 48
  | 64
  | 80
  | 128
  | 160
  | 256
  | 320
  | 512
  | 640
  | 1024;

export const avatarConfig = {
  sizes: [
    16, 20, 24, 32, 40, 48, 64, 80, 128, 160, 256, 320, 512, 640, 1024
  ] as AvatarSize[],
  maxSize: 2048,
  type: 'image/jpeg'
};

export function hiResSize(size: AvatarSize): AvatarSize {
  if (size >= 640) {
    return size;
  }
  return (size * 2) as AvatarSize;
}

export type AvatarStatus = 'none' | 'inactive' | 'out-of-office';

export interface AvatarEntity {
  type: 'user' | 'organization';
  id: string;
  login: string;
  avatar_id: string | null;
  status: AvatarStatus;
}

export function avatar(
  entity: AvatarEntity | undefined | null,
  size: AvatarSize,
  classes: string | null = null,
  fluid = false
): TemplateResult {
  const id = null;
  let content: TemplateResult;
  if (!entity || entity.id === anonymousUserId) {
    content = anonymousAvatar(size, 'Avatar-image', id, fluid);
  } else if (
    entity.id === cumulusSystemUserId ||
    entity.id === cumulusSystemOrganizationId ||
    entity.id === cumulusParticipantOrganizationId ||
    cumulusSupportLoginRegex.test(entity.login)
  ) {
    content = logoMark({ classes: 'Avatar-image Avatar-image--logo' });
  } else if (entity.avatar_id) {
    content = html`<img
      class="Avatar-image"
      ${attr('id', id)}
      src="${avatarHref(entity.avatar_id, hiResSize(size))}"
      alt="${entity.login}"
      ${attr('width', fluid ? null : size.toFixed())}
      ${attr('height', fluid ? null : size.toFixed())}
      loading="lazy"
    />`;
  } else {
    content = identicon(entity, size, 'Avatar-image', id, fluid);
  }
  const status: AvatarStatus =
    entity?.id === anonymousUserId || entity?.id === cumulusSystemUserId
      ? 'none'
      : (entity?.status ?? 'none');
  let statusIcon: TemplateResult | null;
  const statusSize =
    size === 20 ? '45%' : size <= 48 ? '40%' : size <= 64 ? '30%' : '25%';
  switch (status) {
    case 'inactive':
      statusIcon = icon(xCircleFillIcon12, {
        classes: 'Avatar-status color-fg-danger',
        size: statusSize,
        viewBox: 12,
        octicon: false
      });
      break;
    case 'out-of-office':
      statusIcon = icon(arrowLeftCircleIcon, {
        classes: 'Avatar-status color-fg-ooo',
        size: statusSize,
        octicon: false
      });
      break;
    default:
      statusIcon = null;
  }

  return html`<span
    class="Avatar ${classes}"
    ${attr('style', fluid ? null : `width: ${size}px; height: ${size}px`)}
    >${content}${statusIcon}</span
  >`;
}

export function avatarHref(avatar_id: string, size: AvatarSize) {
  return `/image-delivery/${avatar_id}/${size}`;
}

function anonymousAvatar(
  size: AvatarSize,
  classes: string | null = null,
  id: string | null = null,
  fluid = false
) {
  return html`<svg
    viewBox="0 0 16 16"
    class="${classes} color-bg-subtle color-fg-muted"
    ${attr('id', id)}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    alt="anonymous"
    ${attr('width', fluid ? null : size.toFixed())}
    ${attr('height', fluid ? null : size.toFixed())}
    style="padding: 8%"
  >
    <path
      fill-rule="evenodd"
      d="M13 14s1 0 1-1-1-4-6-4-6 3-6 4 1 1 1 1h10zm-9.995-.944v-.002.002zM3.022 13h9.956a.274.274 0 0 0 .014-.002l.008-.002c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664a1.05 1.05 0 0 0 .022.004zm9.974.056v-.002.002zM8 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm3-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"
    ></path>
  </svg>`;
}
